
import { useState, useEffect } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CreateFAQForm } from "./CreateFAQForm";
import { FAQEditForm } from "./FAQEditForm";
import { FAQActions } from "./FAQActions";
import { FAQSyncStatus } from "./FAQSyncStatus";
import { useFAQOperations } from "@/hooks/useFAQOperations";

interface FAQItemData {
  id: string;
  question: string;
  answer: string;
  position: number;
  created_at: string;
  updated_at: string;
  sent_to_frank?: boolean;
  sent_at?: string;
  frank_sync_attempts?: number;
  sync_enabled?: boolean;
  last_sync_attempt?: string;
  sync_error_message?: string;
}

interface FAQSectionProps {
  organizationId: string;
  sectionId: string;
  isAdmin?: boolean;
}

export const FAQSection = ({ organizationId, sectionId, isAdmin = false }: FAQSectionProps) => {
  const [faqItems, setFaqItems] = useState<FAQItemData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  const loadFAQItems = async () => {
    setIsLoading(true);
    try {
    const { data: faqItems, error } = await supabase
      .from('faq_items')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('section_id', sectionId)
      .order('position', { ascending: true });

      if (error) throw error;
      setFaqItems(faqItems || []);
    } catch (error: any) {
      console.error('Error loading FAQ items:', error);
      toast.error('Failed to load FAQ items');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId && sectionId) {
      loadFAQItems();
    }
  }, [organizationId, sectionId]);

  const toggleExpanded = (itemId: string) => {
    // If clicking on the already expanded item, close it
    // If clicking on a different item, open it (closing the previous one)
    setExpandedItem(expandedItem === itemId ? null : itemId);
  };

  // Don't show FAQ section if no items exist and user is not admin
  if (!isAdmin && faqItems.length === 0 && !isLoading) {
    return null;
  }

  return (
    <div className="p-2 sm:p-3 lg:p-4">
      {isLoading ? (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="ml-2 text-muted-foreground">Loading FAQ items...</span>
        </div>
      ) : (
        <div className="space-y-4 sm:space-y-6">
          {isAdmin && (
            <div className="mb-6">
              <CreateFAQForm 
                organizationId={organizationId} 
                sectionId={sectionId}
                onFAQCreated={loadFAQItems}
              />
            </div>
          )}

          {faqItems.length > 0 ? (
        <div className="space-y-2">
          {faqItems.map((item, index) => (
            <div key={item.id} className="bg-white border border-border rounded-lg shadow-sm">
              <FAQItemCard 
                item={item}
                isAdmin={isAdmin}
                isExpanded={expandedItem === item.id}
                onToggle={() => toggleExpanded(item.id)}
                onUpdate={loadFAQItems}
                onDelete={loadFAQItems}
                organizationId={organizationId}
              />
            </div>
          ))}
        </div>
          ) : !isAdmin ? null : (
            <p className="text-muted-foreground text-center py-8 text-base">
              No FAQ items yet. Add your first FAQ item above.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

interface FAQItemCardProps {
  item: FAQItemData;
  isAdmin: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onUpdate: () => void;
  onDelete: () => void;
  organizationId?: string;
}

const FAQItemCard = ({ item, isAdmin, isExpanded, onToggle, onUpdate, onDelete, organizationId }: FAQItemCardProps) => {
  const {
    isEditing,
    isLoading,
    setIsEditing,
    handleSave,
    handleDelete,
    handleCancel
  } = useFAQOperations({ item, organizationId, onUpdate, onDelete });

  return (
    <>
      <div 
        className="flex cursor-pointer items-center justify-between p-4 hover:bg-gray-50 transition-colors touch-manipulation" 
        onClick={onToggle}
      >
        <h2 className="text-lg font-semibold text-gray-800 flex-1 pr-2">
          {item.question}
        </h2>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isAdmin && (
            <div onClick={(e) => e.stopPropagation()}>
              <FAQActions
                isEditing={isEditing}
                isLoading={isLoading}
                onEdit={() => setIsEditing(true)}
                onDelete={handleDelete}
              />
            </div>
          )}
          <div className={`text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
            <ChevronDown className="h-5 w-5" />
          </div>
        </div>
      </div>
      
      {isExpanded && (
        <div className="p-4 bg-gray-50">
          {isEditing ? (
            <FAQEditForm
              question={item.question}
              answer={item.answer}
              isLoading={isLoading}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          ) : (
            <div className="space-y-4">
              <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {item.answer}
              </div>
              {isAdmin && (
                <>
                  <FAQSyncStatus 
                    sentToFrank={item.sent_to_frank}
                    sentAt={item.sent_at}
                    syncErrorMessage={item.sync_error_message}
                    lastSyncAttempt={item.last_sync_attempt}
                    frankSyncAttempts={item.frank_sync_attempts}
                  />
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs text-gray-500 pt-3 border-t border-gray-200">
                    <span>Created: {new Date(item.created_at).toLocaleDateString()}</span>
                    {item.updated_at !== item.created_at && (
                      <span>Updated: {new Date(item.updated_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};
